import {
  useModalContext,
  WSButton,
  WSElement,
  WSElementProps,
  WSForm,
  WSModal,
  WSSelect,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import {
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import {
  AccountStatus,
  AccountType,
  AccountUsage,
  IAccount
} from "@wingspanhq/users/dist/lib/interfaces";
import { IAccountRequirementsResponse } from "@wingspanhq/users/dist/lib/interfaces/api/account-requirements";
import { AxiosError } from "axios";
import React, { useState } from "react";
import * as Yup from "yup";
import { ManualAccountForm } from "../../../components/ManualAccountFormPreview/ManualAccountFormPreview";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { CURRENCY_OPTIONS } from "../../../constants/user";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_USERS_ACCOUNTS } from "../../../query/users/keys";
import { useCreateAccount } from "../../../query/users/mutations";
import { useAccounts, useMemberProfile } from "../../../query/users/queries";
import {
  getCountryName,
  getMemberCountry
} from "../../../query/users/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { paymentsService } from "../../../services/payments";
import { usersService } from "../../../services/users";
import { useMemberPlaidLink } from "../../../utils/usePlaidLink";
import { INVOICE_ACCOUNT_NAME } from "../../utils";
import {
  ChooseAccountForPayoutsModal,
  CHOOSE_ACCOUNT_FOR_PAYOUTS_MODAL
} from "../ChooseAccountForPayoutsModal/ChooseAccountForPayoutsModal";

const BANK_ACCOUNT_MANUAL_FORM = "BANK_ACCOUNT_MANUAL_FORM";
const LOCAL_BANK_ACCOUNT_MANUAL_FORM = "LOCAL_BANK_ACCOUNT_MANUAL_FORM";

type LocalBankAccountManualFormValues = {
  accountNumber: string;
  confirmAccountNumber: string;
  routing: string;
  routingSecondary: string;
};

type Props = { onSubmit: () => void } & WSElementProps;

export const SetupInternationalAccount: React.FC<Props> = ({
  onSubmit,
  ...elementProps
}) => {
  const userId = useUserId();
  const [createAccount, createAccountMeta] = useCreateAccount();
  const accountsQuery = useAccounts();
  const { openModal, closeModal } = useModalContext();
  const memberQuery = useMemberProfile(userId);

  const plaidHandler = useMemberPlaidLink({
    onSuccess: async (publicToken: string) => {
      const accounts = (await createAccount({
        publicToken,
        status: AccountStatus.Pending
      })) as IAccount[];

      if (accounts) {
        openModal(CHOOSE_ACCOUNT_FOR_PAYOUTS_MODAL);

        return {
          success: true,
          data: accounts
        };
      } else {
        return {
          success: false
        };
      }
    }
  });

  return (
    <WSElement {...elementProps}>
      <WSQueries queries={{ memberQuery }}>
        {({ memberQuery: { data: member } }) => (
          <>
            <WSText.Heading3 mb="XS">
              My bank account is in {getCountryName(getMemberCountry(member))}
            </WSText.Heading3>

            <WSText mb="M">
              Wingspan will convert all US dollar deposits into your local
              currency and deposit it to your bank account.
            </WSText>

            <WSButton
              mb="L"
              fullWidth
              name="enterLocalAccountInfo"
              onClick={() => {
                openModal(LOCAL_BANK_ACCOUNT_MANUAL_FORM);
              }}
            >
              Enter local account info
            </WSButton>

            <WSText.Heading3 mb="XS">
              I'm using a US based account
            </WSText.Heading3>

            <WSText mb="M">
              Wingspan uses Plaid to verify your bank account.
            </WSText>
            <WSText mb="M">
              Plaid partners with thousands of financial institutions and is
              trusted by companies like Venmo, Betterment, and Alto Pharmacy.
            </WSText>

            <WSErrorMessage
              mb="M"
              contextKey="PaymentsSetup"
              error={createAccountMeta.error}
            />

            <WSButton.Secondary
              onClick={() => {
                const accounts = accountsQuery.data || [];

                const filteredAccounts = accounts.filter(
                  account =>
                    account.type === AccountType.Depository &&
                    account.canBeUsedFor?.payouts
                );

                if (filteredAccounts.length > 0) {
                  openModal(CHOOSE_ACCOUNT_FOR_PAYOUTS_MODAL);
                } else {
                  plaidHandler.open();
                }
              }}
              fullWidth
              mb="M"
              name="connectPlaid"
            >
              Link account using Plaid
            </WSButton.Secondary>
            <WSButton.Link
              name="connectManually"
              fullWidth
              onClick={() => {
                openModal(BANK_ACCOUNT_MANUAL_FORM);
              }}
            >
              Manually enter account info
            </WSButton.Link>
          </>
        )}
      </WSQueries>

      <ChooseAccountForPayoutsModal
        onSuccess={() => {
          closeModal(CHOOSE_ACCOUNT_FOR_PAYOUTS_MODAL);

          onSubmit();
        }}
      />

      <WSModal
        name={BANK_ACCOUNT_MANUAL_FORM}
        size="S"
        title="Where should we pay you?"
      >
        <WSText mb="M">
          Choose the bank account where we will deposit your funds.
        </WSText>

        <ManualAccountForm
          onSubmit={() => {
            closeModal(BANK_ACCOUNT_MANUAL_FORM);
            onSubmit();
          }}
          submitButtonText="Make direct deposit account"
          submitButtonName="createAccount"
        />
      </WSModal>

      <WSModal
        name={LOCAL_BANK_ACCOUNT_MANUAL_FORM}
        size="S"
        title="Where should we pay you?"
      >
        <LocalAccountCreationForm
          onSubmit={() => {
            closeModal(LOCAL_BANK_ACCOUNT_MANUAL_FORM);
            onSubmit();
          }}
        />
      </WSModal>
    </WSElement>
  );
};

export const LocalAccountCreationForm: React.FC<{ onSubmit: () => void }> = ({
  onSubmit
}) => {
  const userId = useUserId();
  const memberQuery = useMemberProfile(userId);

  const [currency, setCurrency] = useState("");

  const [
    manuallyCreateLocalAccount,
    manuallyCreateLocalAccountMeta
  ] = useWSMutation<
    any,
    AxiosError,
    { values: LocalBankAccountManualFormValues }
  >(
    async ({ values }) => {
      const member = await usersService.member.get(userId);

      const accounts = await usersService.account.create({
        name: INVOICE_ACCOUNT_NAME,
        status: AccountStatus.Active,
        type: AccountType.Depository,
        numbers: {
          account: values.accountNumber,
          routing: values.routing,
          routingSecondary: values.routingSecondary
        },
        usage: AccountUsage.Mixed,
        country: member.profile.address?.country,
        currency: currency
      });

      const accountId = Array.isArray(accounts)
        ? accounts[0].accountId
        : accounts.accountId;

      await paymentsService.payoutSettings.update(userId, {
        payoutPreferences: PayoutPreferences.Standard,
        standard: {
          destinationType: PayoutDestinationType.Account,
          destinationId: accountId
        }
      });
    },
    {
      dependencies: [QUERY_USERS_ACCOUNTS],
      onSuccess: () => {
        onSubmit();
      }
    }
  );

  const [getAccountRequirements, getAccountRequirementsMeta] = useWSMutation<
    IAccountRequirementsResponse,
    AxiosError,
    { currency: string }
  >(async ({ currency }) => {
    if (memberQuery.data?.profile.address?.country) {
      return await usersService.accountRequirements.get({
        currency: currency,
        country: memberQuery.data.profile.address?.country
      });
    } else throw new Error("Internal error");
  });

  return (
    <>
      <WSForm.Label>Account currency</WSForm.Label>
      <WSSelect
        mb="M"
        name="currency"
        value={currency}
        options={CURRENCY_OPTIONS}
        error={false}
        onChange={(value: any) => {
          setCurrency(value);
          if (value) {
            getAccountRequirements({ currency: value });
          }
        }}
        searchable
      />

      <WSForm<LocalBankAccountManualFormValues>
        onSubmit={values => {
          manuallyCreateLocalAccount({ values });
        }}
        validationSchema={Yup.object().shape({
          accountNumber: getAccountRequirementsMeta.data
            ? Yup.string()
                .required("Required")
                .matches(
                  new RegExp(
                    getAccountRequirementsMeta.data.accountNumber.format
                  ),
                  "Not valid"
                )
            : Yup.string().required("Required"),
          confirmAccountNumber: Yup.string()
            .oneOf(
              [Yup.ref("accountNumber"), null],
              "Account number should match"
            )
            .required("Required"),
          routing: getAccountRequirementsMeta.data?.routing?.format
            ? Yup.string()
                .required("Required")
                .matches(
                  new RegExp(getAccountRequirementsMeta.data.routing.format),
                  "Not valid"
                )
            : Yup.string(),
          routingSecondary: getAccountRequirementsMeta.data?.routingSecondary
            ? Yup.string()
                .required("Required")
                .matches(
                  new RegExp(
                    getAccountRequirementsMeta.data.routingSecondary.format
                  ),
                  "Not valid"
                )
            : Yup.string()
        })}
      >
        <WSForm.Field
          name="accountNumber"
          label={
            getAccountRequirementsMeta.data?.accountNumber.name ||
            "Account Number"
          }
          component={WSTextInput}
          componentProps={{
            fsExclude: true
          }}
          mb="M"
        />

        <WSForm.Field
          name="confirmAccountNumber"
          label={
            getAccountRequirementsMeta.data?.accountNumber.name
              ? `Confirm ${getAccountRequirementsMeta.data.accountNumber.name}`
              : "Confirm Account Number"
          }
          component={WSTextInput}
          componentProps={{
            fsExclude: true
          }}
          mb="M"
        />

        {getAccountRequirementsMeta.data?.routing && (
          <WSForm.Field
            name="routing"
            label={
              getAccountRequirementsMeta.data?.routing.name || "Bank identifier"
            }
            component={WSTextInput}
            componentProps={{
              fsExclude: true
            }}
            mb="M"
          />
        )}

        {getAccountRequirementsMeta.data?.routingSecondary && (
          <WSForm.Field
            name="routingSecondary"
            label={getAccountRequirementsMeta.data.routingSecondary.name}
            component={WSTextInput}
            componentProps={{
              fsExclude: true
            }}
            mb="M"
          />
        )}

        <WSErrorMessage
          mb="M"
          contextKey="PaymentsSetup"
          error={manuallyCreateLocalAccountMeta.error}
        />

        <WSButton
          fullWidth
          loading={manuallyCreateLocalAccountMeta.isLoading}
          name="createAccount"
        >
          Make direct deposit account
        </WSButton>
      </WSForm>
    </>
  );
};
